import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Send, Loader2, Sparkles, Target, Check, X, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { getAuthHeader, API, api } from '../utils/api';

const SUGGESTED_PROMPTS = [
  "Can you explain this in simpler words?",
  "Why is my answer wrong?",
  "Give me another example",
  "How can I remember this rule?"
];

const TutorChatDialog = ({ open, onOpenChange, questionContext, questionType, level }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Similar question state
  const [similarQuestion, setSimilarQuestion] = useState(null);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [similarAnswer, setSimilarAnswer] = useState('');
  const [similarResult, setSimilarResult] = useState(null); // null | 'correct' | 'incorrect'

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, similarQuestion, similarResult]);

  // Reset chat when dialog opens with new question
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: `Hi there! 👋 I'm Prof.Scaly, your English tutor. I noticed you got that question wrong — no worries, that's how we learn! Ask me anything about this question and I'll help you understand. 📚`
        }
      ]);
    }
  }, [open]);

  // Cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const timer = setTimeout(() => {
        setMessages([]);
        setInput('');
        setIsStreaming(false);
        setSimilarQuestion(null);
        setSimilarAnswer('');
        setSimilarResult(null);
        setLoadingSimilar(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isStreaming) return;

    const userMessage = { role: 'user', content: messageText };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);

    setMessages([...newMessages, { role: 'assistant', content: '' }]);

    try {
      abortControllerRef.current = new AbortController();

      const response = await fetch(`${API}/tutor/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          question_context: questionContext,
          messages: newMessages
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                accumulatedContent += data.content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    role: 'assistant',
                    content: accumulatedContent
                  };
                  return updated;
                });
              }
              if (data.done) {
                setIsStreaming(false);
                return;
              }
              if (data.error) {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    role: 'assistant',
                    content: 'Sorry, I had trouble responding. Please try again!'
                  };
                  return updated;
                });
                setIsStreaming(false);
                return;
              }
            } catch (e) {
              // ignore malformed lines
            }
          }
        }
      }

      setIsStreaming(false);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Chat error:', error);
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try asking again!'
          };
          return updated;
        });
      }
      setIsStreaming(false);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault();
    sendMessage(input);
  };

  const handleSuggestion = (prompt) => {
    sendMessage(prompt);
  };

  const generateSimilarQuestion = async () => {
    setLoadingSimilar(true);
    setSimilarAnswer('');
    setSimilarResult(null);
    try {
      const data = await api.generateQuestion(questionType, level, questionContext.topic);
      setSimilarQuestion(data);
    } catch (error) {
      console.error('Error generating similar question:', error);
      if (error.response?.status === 402) {
        setSimilarQuestion({ error: true, quotaExceeded: true });
      } else {
        setSimilarQuestion({ error: true });
      }
    } finally {
      setLoadingSimilar(false);
    }
  };

  const checkSimilarAnswer = async () => {
    if (!similarAnswer.trim() || !similarQuestion) return;
    const correct = similarAnswer.trim().toLowerCase() === similarQuestion.correct_answer.trim().toLowerCase();
    setSimilarResult(correct ? 'correct' : 'incorrect');
    try {
      const result = await api.recordProgress(questionType, level, correct);
      const streak = result?.streak;
      if (streak?.incremented_today) {
        const days = streak.current_streak;
        toast.success(
          days === 1
            ? '🔥 Streak started! Come back tomorrow to keep it going.'
            : `🔥 ${days}-day streak! Keep it going!`,
          { duration: 4000 }
        );
      }
    } catch (error) {
      console.error('Error recording progress:', error);
    }
  };

  const tryAnotherSimilar = () => {
    setSimilarQuestion(null);
    setSimilarAnswer('');
    setSimilarResult(null);
    generateSimilarQuestion();
  };

  // Show the "Practice Similar" CTA when at least one AI reply has arrived and no similar question in flight
  const showPracticeCTA =
    messages.length >= 2 &&
    messages[messages.length - 1].role === 'assistant' &&
    messages[messages.length - 1].content.length > 0 &&
    !isStreaming &&
    !similarQuestion &&
    !loadingSimilar;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl h-[85vh] flex flex-col p-0 rounded-3xl overflow-hidden gap-0"
        data-testid="tutor-chat-dialog"
      >
        <DialogHeader className="p-6 bg-gradient-to-r from-emerald-50 to-amber-50 border-b-2 border-slate-200">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-14 h-14 object-contain"
              data-testid="tutor-mascot-image"
            />
            <div className="text-left">
              <DialogTitle className="text-2xl font-heading font-bold text-emerald-700" data-testid="tutor-chat-title">
                Ask Prof.Scaly
              </DialogTitle>
              <p className="text-sm text-slate-600 font-body">Your friendly AI English tutor</p>
            </div>
          </div>
        </DialogHeader>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50" data-testid="tutor-messages-container">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`tutor-message-${index}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-4 ${
                    message.role === 'user'
                      ? 'bg-emerald-500 text-white rounded-tr-sm'
                      : 'bg-white border-2 border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-emerald-100 rounded-full p-1">
                        <Sparkles className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />
                      </div>
                      <span className="text-xs font-bold text-emerald-700">Prof.Scaly</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed font-body">
                    {message.content || (
                      isStreaming && index === messages.length - 1 && (
                        <span className="inline-flex items-center gap-1">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-slate-500">Thinking...</span>
                        </span>
                      )
                    )}
                    {isStreaming && index === messages.length - 1 && message.content && (
                      <span className="inline-block w-2 h-4 bg-emerald-500 ml-1 animate-pulse" />
                    )}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Suggested prompts - only show initially */}
          {messages.length === 1 && !isStreaming && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="pt-2"
              data-testid="tutor-suggestions"
            >
              <p className="text-xs text-slate-500 font-semibold mb-2 px-2">💡 Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestion(prompt)}
                    className="text-xs bg-white border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 text-emerald-700 rounded-full px-3 py-2 font-semibold transition-colors"
                    data-testid={`tutor-suggestion-${i}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Practice Similar Question CTA */}
          {showPracticeCTA && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="pt-2"
              data-testid="practice-similar-cta"
            >
              <button
                onClick={generateSimilarQuestion}
                className="w-full bg-gradient-to-r from-amber-100 to-emerald-100 hover:from-amber-200 hover:to-emerald-200 border-2 border-emerald-300 rounded-2xl p-4 flex items-center gap-3 transition-all group"
                data-testid="practice-similar-button"
              >
                <div className="bg-white rounded-full p-2 group-hover:scale-110 transition-transform">
                  <Target className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
                </div>
                <div className="text-left flex-1">
                  <p className="font-heading font-bold text-slate-800 text-sm">
                    Practice a Similar Question
                  </p>
                  <p className="text-xs text-slate-600 font-body">
                    Try one on the same topic to reinforce what you learned
                  </p>
                </div>
              </button>
            </motion.div>
          )}

          {/* Loading similar question */}
          {loadingSimilar && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white border-2 border-amber-200 rounded-2xl p-6 text-center"
              data-testid="similar-loading"
            >
              <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-2" />
              <p className="text-sm text-slate-600 font-semibold">Prof.Scaly is preparing your practice question...</p>
            </motion.div>
          )}

          {/* Similar Question Card */}
          {similarQuestion && !similarQuestion.error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border-2 border-emerald-300 rounded-2xl p-5 shadow-sm"
              data-testid="similar-question-card"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="bg-emerald-100 rounded-full p-1.5">
                  <Target className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                </div>
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">
                  Practice Question
                </span>
                <div className="ml-auto bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold" data-testid="similar-topic">
                  {similarQuestion.topic}
                </div>
              </div>

              <p className="text-slate-800 font-body text-sm mb-4 whitespace-pre-wrap" data-testid="similar-question-text">
                {similarQuestion.question_text}
              </p>

              {similarQuestion.options ? (
                <div className="space-y-2 mb-3">
                  {similarQuestion.options.map((option, index) => {
                    const isSelected = similarAnswer === option;
                    const isCorrectOption = similarResult && option === similarQuestion.correct_answer;
                    const isWrongSelected = similarResult === 'incorrect' && isSelected;

                    return (
                      <button
                        key={index}
                        onClick={() => !similarResult && setSimilarAnswer(option)}
                        disabled={!!similarResult}
                        className={`w-full text-left p-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                          isCorrectOption
                            ? 'bg-green-50 border-green-400 text-green-700'
                            : isWrongSelected
                            ? 'bg-red-50 border-red-400 text-red-700'
                            : isSelected
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
                        }`}
                        data-testid={`similar-option-${index}`}
                      >
                        {option}
                        {isCorrectOption && <Check className="w-4 h-4 inline ml-2" strokeWidth={3} />}
                        {isWrongSelected && <X className="w-4 h-4 inline ml-2" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mb-3">
                  <input
                    type="text"
                    value={similarAnswer}
                    onChange={(e) => setSimilarAnswer(e.target.value)}
                    disabled={!!similarResult}
                    className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:outline-none text-sm disabled:opacity-70"
                    placeholder="Type your answer..."
                    data-testid="similar-answer-input"
                  />
                </div>
              )}

              {!similarResult && (
                <Button
                  onClick={checkSimilarAnswer}
                  disabled={!similarAnswer.trim()}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-2 text-sm font-bold disabled:opacity-50"
                  data-testid="similar-check-button"
                >
                  Check Answer
                </Button>
              )}

              {similarResult && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 space-y-2"
                >
                  <div
                    className={`p-3 rounded-xl border-2 ${
                      similarResult === 'correct'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                    data-testid="similar-result"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {similarResult === 'correct' ? (
                        <>
                          <div className="bg-green-500 rounded-full p-1">
                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                          </div>
                          <p className="font-heading font-bold text-green-700 text-sm">
                            Excellent! You've got it! 🎉
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="bg-red-500 rounded-full p-1">
                            <X className="w-3 h-3 text-white" strokeWidth={3} />
                          </div>
                          <p className="font-heading font-bold text-red-700 text-sm">
                            Not quite — keep going!
                          </p>
                        </>
                      )}
                    </div>
                    {similarResult === 'incorrect' && (
                      <p className="text-xs text-slate-700 mb-1">
                        <span className="font-bold">Correct:</span> {similarQuestion.correct_answer}
                      </p>
                    )}
                    <p className="text-xs text-slate-700 font-body">
                      <span className="font-bold">Why:</span> {similarQuestion.explanation}
                    </p>
                  </div>

                  <Button
                    onClick={tryAnotherSimilar}
                    variant="outline"
                    className="w-full rounded-full py-2 text-xs font-bold border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="similar-try-another-button"
                  >
                    <RefreshCw className="w-3 h-3 mr-2" />
                    Try Another Similar Question
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}

          {similarQuestion && similarQuestion.error && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-center" data-testid="similar-error">
              {similarQuestion.quotaExceeded ? (
                <>
                  <p className="text-sm text-amber-800 mb-2 font-semibold">
                    You've used all 50 free practices for this topic. Upgrade to Premium for unlimited practice!
                  </p>
                  <button
                    onClick={() => window.location.href = '/subscription'}
                    className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-4 py-2 font-bold"
                    data-testid="similar-upgrade-button"
                  >
                    Upgrade to Premium
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-red-700 mb-2">Sorry, couldn't generate a practice question right now.</p>
                  <button
                    onClick={generateSimilarQuestion}
                    className="text-xs text-red-600 underline font-semibold"
                  >
                    Try again
                  </button>
                </>
              )}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form
          onSubmit={handleSend}
          className="p-4 bg-white border-t-2 border-slate-200 flex gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a follow-up question..."
            disabled={isStreaming}
            className="flex-1 px-4 py-3 rounded-full border-2 border-slate-200 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 text-sm font-body disabled:opacity-50"
            data-testid="tutor-input"
          />
          <Button
            type="submit"
            disabled={!input.trim() || isStreaming}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 tactile-button border-emerald-700 disabled:opacity-50"
            data-testid="tutor-send-button"
          >
            {isStreaming ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TutorChatDialog;
